import React from 'react';
import { CheckCircle2Icon, AlertTriangleIcon, MapPinOffIcon, PlusIcon, ArrowUpIcon, ArrowDownIcon, Trash2Icon } from 'lucide-react';
import { defaultAccess, useFunnel } from '../../contexts/FunnelContext';
import type { StepErrors } from '../../utils/validation';
import type { AddressValue, ExtraStop, FunnelData, StopRole } from '../../types/funnel';
import { StepHeading } from './StepHeading';
import { TextField } from './TextField';
import { Segmented } from './Segmented';
import { AddressFields } from './AddressFields';
import { HelpAnswers } from './HelpAnswers';
const copy = {
  en: {
    stopsTitle: 'Extra stops',
    stopsSub: 'Picking up or dropping off somewhere else on the way? Add each stop in route order.',
    add: 'Add an extra stop',
    stop: (n: number) => `Stop ${n}`,
    role: 'At this stop we…',
    pickup: 'Pick up',
    dropoff: 'Drop off',
    both: 'Both',
    items: 'What gets loaded or unloaded here?',
    up: 'Move stop up',
    down: 'Move stop down',
    remove: 'Remove stop',
    route: 'Route order'
  },
  es: {
    stopsTitle: 'Paradas adicionales',
    stopsSub: '¿Recogemos o entregamos en otro lugar en el camino? Agregue cada parada en el orden de la ruta.',
    add: 'Agregar una parada',
    stop: (n: number) => `Parada ${n}`,
    role: 'En esta parada…',
    pickup: 'Recogemos',
    dropoff: 'Entregamos',
    both: 'Ambas',
    items: '¿Qué se carga o descarga aquí?',
    up: 'Subir parada',
    down: 'Bajar parada',
    remove: 'Quitar parada',
    route: 'Orden de la ruta'
  }
};
function originValue(d: FunnelData): AddressValue {
  return {
    street: d.originStreet,
    unit: d.originUnit,
    building: d.originBuilding,
    city: d.originCity,
    state: d.originState,
    zip: d.originZip,
    placeId: d.originPlaceId,
    status: d.originAddrStatus
  };
}
function destValue(d: FunnelData): AddressValue {
  return {
    street: d.destStreet,
    unit: d.destUnit,
    building: d.destBuilding,
    city: d.destCity,
    state: d.destState,
    zip: d.destZip,
    placeId: d.destPlaceId,
    status: d.destAddrStatus
  };
}
function toFlat(prefix: 'origin' | 'dest', p: Partial<AddressValue>): Partial<FunnelData> {
  const out: Record<string, unknown> = {};
  const map: Record<keyof AddressValue, string> = {
    street: 'Street',
    unit: 'Unit',
    building: 'Building',
    city: 'City',
    state: 'State',
    zip: 'Zip',
    placeId: 'PlaceId',
    status: 'AddrStatus'
  };
  for (const [k, v] of Object.entries(p)) out[`${prefix}${map[k as keyof AddressValue]}`] = v;
  return out as Partial<FunnelData>;
}
export function StepAddresses({
  errors
}: {
  errors: StepErrors;
}) {
  const {
    t,
    lang,
    data,
    update,
    quote
  } = useFunnel();
  const c = copy[lang];
  const err = (k: string) => errors[k] ? t(errors[k]!) : undefined;
  const zoneMsg = quote.zone === 'eligible_prelim' ? {
    cls: 'border-lime-dark/30 bg-lime-soft',
    icon: <CheckCircle2Icon className="h-5 w-5 text-lime-dark" data-id="element-638" data-mp="2usesuhbdt1q9" />,
    text: t('zoneEligible')
  } : quote.zone === 'review' ? {
    cls: 'border-ink-300 bg-ink-100',
    icon: <AlertTriangleIcon className="h-5 w-5 text-ink-600" data-id="element-639" data-mp="n4tubz2x2e08" />,
    text: t('zoneReview')
  } : quote.zone === 'outside' ? {
    cls: 'border-magenta/30 bg-magenta-soft',
    icon: <MapPinOffIcon className="h-5 w-5 text-magenta-dark" data-id="element-640" data-mp="wv4zfoyc6t8p" />,
    text: t('zoneOutside')
  } : null;
  const setStops = (stops: ExtraStop[]) => update({
    stops
  });
  const patchStop = (id: string, p: Partial<ExtraStop>) => setStops(data.stops.map(s => s.id === id ? {
    ...s,
    ...p
  } : s));
  const move = (i: number, dir: -1 | 1) => {
    const next = [...data.stops];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setStops(next);
  };
  const addStop = () => setStops([...data.stops, {
    id: `st${Date.now()}`,
    street: '',
    unit: '',
    building: '',
    city: '',
    state: '',
    zip: '',
    placeId: null,
    status: 'typed',
    role: 'pickup',
    items: '',
    access: {
      ...defaultAccess
    }
  }]);
  return <div data-id="element-641" data-mp="3co41bnij7zk7">
      <StepHeading title={t('addrTitle')} intro={t('addrIntro')} data-id="element-642" data-mp="3conhayomgdvl" />
      <div className="grid gap-10 md:grid-cols-2" data-id="element-643" data-mp="3f067ufx67e4h">
        <fieldset data-id="element-644" data-mp="3j29ij2c0meop">
          <legend className="mb-3 font-display text-lg font-extrabold text-ink" data-id="element-645" data-mp="3j1rq6gsh4km2">{t('fromLabel')}</legend>
          <AddressFields value={originValue(data)} onChange={p => update(toFlat('origin', p))} lang={lang} zipError={err('originZip')} data-id="element-646" data-mp="3o1clmh8fqau1" />
        </fieldset>
        <fieldset data-id="element-647" data-mp="3sycuezwo5rou">
          <legend className="mb-3 font-display text-lg font-extrabold text-ink" data-id="element-648" data-mp="2vd8hi05ii5tg">{t('toLabel')}</legend>
          <AddressFields value={destValue(data)} onChange={p => update(toFlat('dest', p))} lang={lang} zipError={err('destZip')} data-id="element-649" data-mp="30bicoar8l6f2" />
        </fieldset>
      </div>

      {zoneMsg && <div role="status" className={`mt-6 flex items-start gap-3 rounded-xl border p-4 text-[15px] leading-relaxed text-ink ${zoneMsg.cls}`} data-id="element-650" data-mp="3a1uv9xfhqpik">
          <span className="mt-0.5 shrink-0" data-id="element-651" data-mp="2clo07uatxjvc">{zoneMsg.icon}</span>
          {zoneMsg.text}
        </div>}

      <section className="mt-10 border-t border-ink-200 pt-8" data-id="element-652" data-mp="2hjzcebolk6gq">
        <h2 className="font-display text-xl font-extrabold text-ink" data-id="element-653" data-mp="2a2awe4qo5kny">{c.stopsTitle}</h2>
        <p className="mt-1 text-sm text-ink-500" data-id="element-654" data-mp="2537sinmfdkhk">{c.stopsSub}</p>

        {data.stops.length > 0 && <ol aria-label={c.route} className="mt-5 space-y-5" data-id="element-655" data-mp="203bvx9g24vc1">
            {data.stops.map((s, i) => <li key={s.id} className="rounded-2xl bg-white p-4 ring-1 ring-ink-200 sm:p-5" data-id="element-656" data-mp="1v5jtvmlp7su4" data-mpi={i}>
                <div className="mb-4 flex items-center gap-2" data-id="element-657" data-mp="1r3owgt6w1zlm">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-ink text-sm font-bold text-lime" data-id="element-658" data-mp="1r4zyookoqzam">{i + 1}</span>
                  <h3 className="flex-1 font-display text-lg font-extrabold text-ink" data-id="element-659" data-mp="1hckgpr9e2apq">{c.stop(i + 1)}</h3>
                  <button type="button" aria-label={c.up} disabled={i === 0} onClick={() => move(i, -1)} className="flex h-10 w-10 items-center justify-center rounded-lg border border-ink-200 hover:bg-ink-100 disabled:opacity-30" data-id="element-660" data-mp="1cf26zynjrrgo">
                    <ArrowUpIcon className="h-4 w-4" data-id="element-661" data-mp="13ee15r8b4xev" />
                  </button>
                  <button type="button" aria-label={c.down} disabled={i === data.stops.length - 1} onClick={() => move(i, 1)} className="flex h-10 w-10 items-center justify-center rounded-lg border border-ink-200 hover:bg-ink-100 disabled:opacity-30" data-id="element-662" data-mp="srpj7iwgbbjr">
                    <ArrowDownIcon className="h-4 w-4" data-id="element-663" data-mp="oqvqmc82d8kj" />
                  </button>
                  <button type="button" aria-label={c.remove} onClick={() => setStops(data.stops.filter(x => x.id !== s.id))} className="flex h-10 w-10 items-center justify-center rounded-lg text-ink-500 hover:bg-ink-100 hover:text-ink" data-id="element-664" data-mp="osqfg1mghktw">
                    <Trash2Icon className="h-4 w-4" data-id="element-665" data-mp="12rgbrf1gbgh7" />
                  </button>
                </div>
                <AddressFields value={s} onChange={p => patchStop(s.id, p)} lang={lang} zipError={err(`stop_${s.id}`)} data-id="element-666" data-mp="17oybqfxhqld2" />
                <div className="mt-4 grid gap-4 md:grid-cols-[auto_1fr]" data-id="element-667" data-mp="1vdrccpp25he0">
                  <Segmented<StopRole> label={c.role} value={s.role} onChange={v => patchStop(s.id, {
              role: v
            })} options={[{
              value: 'pickup',
              label: c.pickup
            }, {
              value: 'dropoff',
              label: c.dropoff
            }, {
              value: 'both',
              label: c.both
            }]} data-id="element-668" data-mp="206an7ewd1yf4" />
                  <TextField label={c.items} value={s.items} onChange={v => patchStop(s.id, {
              items: v.slice(0, 200)
            })} data-id="element-669" data-mp="2ec4a2loo5orf" />
                </div>
              </li>)}
          </ol>}

        <button type="button" onClick={addStop} className="mt-5 inline-flex min-h-[48px] items-center gap-2 rounded-xl border-2 border-dashed border-ink-300 bg-white px-4 font-semibold text-ink hover:border-ink" data-id="element-670" data-mp="1mlzleatvmfke">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-671" data-mp="1vn61wjwizqdn" />
          {c.add}
        </button>
      </section>

      <HelpAnswers screen="stepAddresses" className="mt-10" data-id="element-672" data-mp="3c161ftrgle0d" />
    </div>;
}